import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Thermometer, FileCheck2, GraduationCap, ShieldCheck, BadgeCheck } from 'lucide-react'
import { HeroSection } from '@/components/sections/HeroSection'
import ScrollingLogos from '@/components/sections/ScrollingLogos'
import { Section } from '@/components/ui/Section'
import { Container } from '@/components/ui/Container'

const gdpFeatures = [
  { key: 'temperature', icon: Thermometer },
  { key: 'documentation', icon: FileCheck2 },
  { key: 'training', icon: GraduationCap },
] as const

const certificateImages: Record<string, string> = {
  ifs: '/images/certificates/ifs-logistics.png',
  iso9001: '/images/certificates/iso-9001.png',
  iso45001: '/images/certificates/iso-45001.png',
  adr: '/images/certificates/adr.png',
  gdp: '/images/certificates/gdp.jpg',
}

const certificateKeys = ['ifs', 'iso9001', 'iso45001', 'adr', 'gdp']

export default function Certifications() {
  const { t, i18n } = useTranslation('common')

  const logos = certificateKeys.map((key) => ({
    key,
    imageSrc: certificateImages[key],
    name: t(`certifications.badges.${key}.name`),
  }))

  return (
    <>
      <Helmet>
        <title>{t('certifications.seo.title')}</title>
        <meta name="description" content={t('certifications.seo.description')} />
        <html lang={i18n.language} />
      </Helmet>

      <HeroSection
        imageSrc="/images/certifications-hero.jpg"
        title={t('certifications.hero.title')}
        subtitle={t('certifications.hero.subtitle')}
        slogan={t('certifications.hero.slogan')}
        primaryCta={{ label: t('buttons.contactUs'), to: 'contacts' }}
        secondaryCta={{ label: t('nav.services'), to: 'services' }}
      />

      <ScrollingLogos
        title={t('certifications.partners.heading')}
        subtitle={t('certifications.partners.body')}
        logos={logos}
        variant="dark"
      />

      <Section variant="light">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-5 relative"
            >
              <div className="absolute -inset-4 bg-brand-red/5 rounded-3xl rotate-2" aria-hidden />
              <div className="relative bg-white rounded-2xl border border-gray-100 shadow-xl p-6 md:p-8 -rotate-1">
                <img
                  src={certificateImages.gdp}
                  alt={t('certifications.gdp.heading')}
                  className="w-full h-auto rounded-lg object-contain"
                />
                <div className="absolute -top-4 -right-4 bg-brand-red text-white rounded-full px-4 py-2 flex items-center gap-2 shadow-lg rotate-3">
                  <BadgeCheck className="w-5 h-5" />
                  <span className="text-sm font-bold tracking-wide">CERTIFIED</span>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-3 text-sm text-brand-text/80 pl-2">
                <ShieldCheck className="w-5 h-5 text-brand-red flex-shrink-0" />
                <span>EU 2013/C 343/01 — Good Distribution Practice</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-7"
            >
              <span className="inline-block text-xs font-bold tracking-[0.2em] text-brand-red uppercase mb-4">
                {t('certifications.badges.gdp.name')}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-brand-dark mb-6 leading-tight">
                {t('certifications.gdp.heading')}
              </h2>
              <p className="text-lg text-brand-text leading-relaxed mb-8">
                {t('certifications.gdp.body')}
              </p>

              <div className="grid sm:grid-cols-1 gap-4">
                {gdpFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.key}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                    className="group flex items-start gap-4 bg-white p-5 rounded-xl border border-gray-100 hover:border-brand-red/40 hover:shadow-md transition-all"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-brand-red/10 rounded-lg flex items-center justify-center group-hover:bg-brand-red group-hover:text-white transition-colors">
                      <feature.icon className="w-6 h-6 text-brand-red group-hover:text-white transition-colors" />
                    </div>
                    <div className="pt-1">
                      <p className="text-brand-text leading-relaxed">
                        {t(`certifications.gdp.features.${feature.key}`)}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </Container>
      </Section>
    </>
  )
}
